import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DrawerHeader, { DRAWER_NO_SLIDE, DRAWER_PAPER_PROPS } from "./DrawerHeader";
import { compareVersions } from "../utils/cveVersionUtil";

// Ordre d'affichage des instances : la prod d'abord
const ENV_ORDER = ["PRD", "REC", "INT"];


// Partagé par toutes les lignes : React court-circuite le sous-arbre quand il retrouve
// le même élément à la même position.
const EXPAND_ICON = <ExpandMoreIcon />;

// Styles constants : définis une fois pour toutes, emotion ne les resérialise pas
// à chaque ligne (c'est le poste de coût principal quand on affiche 90 CVE).
const SUMMARY_BOX_SX = {
  display: "flex",
  alignItems: "baseline",
  gap: 2,
  width: "100%",
  flexWrap: "wrap",
};
const CVE_ID_SX = { minWidth: 150 };
const META_SX = { ml: "auto", pr: 1 };
const DETAILS_SX = { pt: 0 };
const DESC_SX = { mb: 1.5 };
const SITES_GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
  columnGap: 2,
  rowGap: 0.5,
  mt: 0.5,
};
const SITES_TITLE_SX = { fontWeight: 600 };
const SITE_ROW_SX = { display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 };

// Le libellé d'instance : monochrome, largeur fixe pour que la colonne s'aligne.
// Pas de taille ni d'interlettrage custom, on reste sur la typo des autres drawers.
const ENV_LABEL_BASE = { flex: "0 0 34px" };

function getSiteVersionNormalized(site) {
  const raw = site?.versionplugins;
  if (!raw) return null;
  return String(raw).trim().replace(/^v/i, "");
}

function getEnv(site) {
  return site?.env ? String(site.env).trim().toUpperCase() : null;
}

function envRank(env) {
  const i = ENV_ORDER.indexOf(env);
  return i === -1 ? ENV_ORDER.length : i;
}

// « 2 PRD · 1 REC » plutôt qu'une série de pastilles colorées
function summarizeEnvs(sites) {
  const counts = {};
  sites.forEach((s) => {
    const env = getEnv(s) || "N/A";
    counts[env] = (counts[env] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => envRank(a[0]) - envRank(b[0]))
    .map(([env, count]) => `${count} ${env}`)
    .join(" · ");
}

function severityLabel(score) {
  if (score == null) return "CVSS n.c.";
  if (score >= 9) return `Critique ${score}`;
  if (score >= 7) return `Élevée ${score}`;
  if (score >= 4) return `Moyenne ${score}`;
  return `Faible ${score}`;
}

// Remplace le Collapse de MUI : celui-ci anime `height` et lit clientHeight de façon
// synchrone, ce qui relayoute la liste entière à chaque dépliage. Ici la hauteur bascule
// d'un coup et le fondu est fait en CSS. Le démontage du contenu replié est implicite.
// forwardRef : MUI passe un ref au slot de transition.
const CVEReveal = React.forwardRef(function CVEReveal({ in: inProp, children }, ref) {
  if (!inProp) return null;
  return (
    <div ref={ref} className="cveReveal">
      {children}
    </div>
  );
});

// Mémoïsé : sans ça, déplier une ligne re-render les ~98 autres. Les quatre props doivent
// rester référentiellement stables, sinon le memo ne sert à rien.
const CVERow = React.memo(function CVERow({ row, expanded, onToggle, sx }) {
  const { cve, sites, isSevere, severity, envSummary, rowId } = row;

  const handleChange = useCallback(
    (event, isOpen) => onToggle(isOpen ? rowId : null),
    [onToggle, rowId]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      className="cveRow"
      TransitionComponent={CVEReveal}
      sx={isSevere ? sx.accordion.severe : sx.accordion.normal}
    >
      <AccordionSummary expandIcon={EXPAND_ICON}>
        <Box sx={SUMMARY_BOX_SX}>
          <Typography variant="body2" sx={CVE_ID_SX}>
            {cve.cve_id}
          </Typography>
          <Typography
            variant="body2"
            sx={isSevere ? sx.severity.severe : sx.severity.normal}
          >
            {severity}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={META_SX}>
            {envSummary}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={DETAILS_SX}>
        <Typography variant="body2" color="text.secondary" sx={DESC_SX}>
          {cve.description || "Pas de description fournie."}
        </Typography>

        {cve.maxversion && (
          <Typography variant="body2" color="text.secondary" sx={DESC_SX}>
            Versions vulnérables : &lt; {cve.maxversion}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" display="block" sx={SITES_TITLE_SX}>
          Sites impactés ({sites.length})
        </Typography>
        <Box sx={SITES_GRID_SX}>
          {sites.map((site, i) => {
            const env = getEnv(site) || "N/A";
            return (
              <Box key={`${site.site}-${env}-${i}`} sx={SITE_ROW_SX}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={env === "PRD" ? sx.envLabel.prd : sx.envLabel.other}
                >
                  {env}
                </Typography>
                <Typography
                  variant="body2"
                  component={site.siteUrl ? "a" : "span"}
                  {...(site.siteUrl
                    ? { href: site.siteUrl, target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  sx={site.siteUrl ? sx.siteLink : sx.siteName}
                >
                  {site.site}
                </Typography>
                {site.versionplugins && (
                  <Typography variant="body2" color="text.secondary">
                    {site.versionplugins}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

const CVEDrawer = ({ selectedPlugin, handleCloseDrawer, role }) => {
  const theme = useTheme();
  const [expandedId, setExpandedId] = useState(null);

  const pluginData = selectedPlugin || null;

  // Pour chaque CVE impactante, les sites dont la version installée est vulnérable.
  const cveRows = useMemo(() => {
    if (!pluginData) return [];
    return (pluginData.impactedCves || [])
      .map((cve) => {
        const cveVersion = cve.maxversion;
        const sites = (pluginData.sites || [])
          .filter((s) => {
            const v = getSiteVersionNormalized(s);
            return v && cveVersion && compareVersions(v, cveVersion) === -1;
          })
          .sort((a, b) => {
            const rank = envRank(getEnv(a)) - envRank(getEnv(b));
            if (rank !== 0) return rank;
            return String(a.site || "").localeCompare(String(b.site || ""));
          });
        const score = cve.severity != null ? cve.severity : null;
        return {
          cve,
          sites,
          // Le rouge est réservé à la criticité : seules les CVE actionnables (>= 7)
          // portent un accent, le reste reste neutre.
          isSevere: score != null && score >= 7,
          severity: severityLabel(score),
          envSummary: summarizeEnvs(sites),
        };
      })
      .filter(({ sites }) => sites.length > 0)
      .sort((a, b) => (b.cve.severity || 0) - (a.cve.severity || 0))
      // Figé après le tri : l'identité d'une ligne ne doit pas bouger d'un render à l'autre.
      .map((row, i) => ({ ...row, rowId: row.cve.cve_id + "-" + i }));
  }, [pluginData]);

  // Objets de style dépendants du thème : calculés une fois, réutilisés par
  // référence pour toutes les lignes.
  const sx = useMemo(
    () => ({
      accordion: {
        severe: {
          borderLeft: `3px solid ${theme.palette.error.main}`,
          "&:before": { display: "none" },
          mb: 0.75,
          borderRadius: 1,
          overflow: "hidden",
        },
        normal: {
          borderLeft: `3px solid ${theme.palette.divider}`,
          "&:before": { display: "none" },
          mb: 0.75,
          borderRadius: 1,
          overflow: "hidden",
        },
      },
      severity: {
        severe: { color: theme.palette.error.main, fontWeight: 600 },
        normal: { color: theme.palette.text.secondary },
      },
      envLabel: {
        prd: { ...ENV_LABEL_BASE, color: theme.palette.text.primary, fontWeight: 700 },
        other: { ...ENV_LABEL_BASE, color: theme.palette.text.secondary },
      },
      siteLink: {
        color: theme.palette.text.primary,
        textDecoration: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "&:hover": { borderBottomColor: theme.palette.text.secondary },
      },
      siteName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }),
    [theme]
  );

  // Le drawer reste monté entre deux plugins : sans reset, expandedId désignerait une
  // ligne du plugin précédent.
  useEffect(() => {
    setExpandedId(null);
  }, [selectedPlugin]);

  if (!pluginData) return null;

  // Toutes les CVE référencées pour ce vendor, impactantes ou non.
  const totalCves = (pluginData.cves || []).length;

  return (
    <Drawer
      anchor="right"
      open={selectedPlugin != null}
      onClose={handleCloseDrawer}
      PaperProps={DRAWER_PAPER_PROPS.compact}
      SlideProps={DRAWER_NO_SLIDE}
      // Sans ça, MUI pose overflow:hidden + padding-right sur <body> à l'ouverture,
      // ce qui force un reflow de tout le dashboard pendant l'animation.
      disableScrollLock
    >
      <div>
        <DrawerHeader
          title={pluginData.name}
          subtitle={`${cveRows.length} CVE impactant vos sites${
            totalCves !== cveRows.length ? ` (sur ${totalCves} référencées)` : ""
          }`}
          onClose={handleCloseDrawer}
          role={role}
        />

        <div className="drawerTab">
          {cveRows.length === 0 ? (
            <Typography color="text.secondary">
              Aucune CVE n'impacte les versions actuellement installées.
            </Typography>
          ) : (
            cveRows.map((row) => (
              <CVERow
                key={row.rowId}
                row={row}
                expanded={expandedId === row.rowId}
                onToggle={setExpandedId}
                sx={sx}
              />
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CVEDrawer;
